import { addQueryArgs } from '@wordpress/url';
import { __, sprintf } from '@wordpress/i18n';
import {
    Card,
    SimpleInput,
    useToast,
    DokanToaster,
} from '@getdokan/dokan-ui';
import {
    DokanButton,
    DokanTab,
    DataViews,
    DateTimeHtml,
    WpDatePicker,
    DokanModal,
    RichText,
    Select,
    SearchInput,
    VendorAsyncSelect,
} from '@dokan/components';
import { useState, useEffect, RawHTML } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { Ticket } from '../type/Ticket';
import { twMerge } from 'tailwind-merge';
import { dateI18n, format, getSettings } from '@wordpress/date';
import { CircleCheck, Plus, ChevronDown } from 'lucide-react';
import { truncate } from '@dokan/utilities';
import { applyFilters } from '@wordpress/hooks';
import { useSelect } from '@wordpress/data';
import NewTicket from './NewTicket';

function List( props ) {
    const { context = 'admin', navigate } = props;
    const currentLogedinVendor = useSelect( ( select ) => {
        if ( context === 'admin' ) {
            return {};
        }

        // @ts-ignore
        return select( 'core' ).getCurrentUser();
    }, [] );
    const [ selection, setSelection ] = useState( [] );
    const [ tickets, setTickets ] = useState< Ticket[] >( [] );
    const [ isLoading, setIsLoading ] = useState( true );
    const [ status, setStatus ] = useState( 'all' );
    const [ searchTerm, setSearchTerm ] = useState( '' );
    const [ selectedVendor, setSelectedVendor ] = useState( null ); // State for vendor filter
    const [ selectedDate, setSelectedDate ] = useState( null ); // State for vendor filter
    const [ counts, setCounts ] = useState( {
        active: 0,
        closed: 0,
        open: 0,
        pending: 0,
        unread: 0,
    } );
    const [ selectedBulkAction, setSelectedBulkAction ] = useState( '' );
    const [ isApplyingBulkAction, setIsApplyingBulkAction ] = useState( false ); // New state for bulk action loading
    const toast = useToast();

    const defaultLayouts = {
        table: { density: 'comfortable' },
        grid: {},
        list: {},
    };
    const bulkOptions = applyFilters( 'dokan-vendor-support-bull-actions', [
        {
            label: __( 'Mark as read', 'dokan' ),
            value: 'mark-all-as-read',
        },
        {
            label: __( 'Delete Tickets older than 6 months', 'dokan' ),
            value: 'delete-last-6-months',
        },
        {
            label: __( 'Delete Tickets older than 1 year', 'dokan' ),
            value: 'delete-last-1-year',
        },
    ] );
    const [ isConfirmationModalOpen, setIsConfirmationModalOpen ] =
        useState( '' );

    const notIsRead = ( item: Ticket ) => {
        if ( context === 'admin' ) {
            return ! item.is_read_by_admin;
        }

        return ! item.is_read_by_vendor;
    };

    const getStatus = ( item: Ticket ) => {
        if ( item.status === 'open' && item?.last_reply_by === 'vendor' ) {
            return (
                <span
                    className={ twMerge(
                        'text-orange-500',
                        isLoading &&
                            'text-transparent bg-neutral-200 animate-pulse rounded'
                    ) }
                >
                    { __( 'Pending', 'dokan' ) }
                </span>
            );
        } else if ( item.status === 'open' ) {
            return (
                <span
                    className={ twMerge(
                        'text-green-500',
                        isLoading &&
                            'text-transparent bg-neutral-200 animate-pulse rounded'
                    ) }
                >
                    { __( 'Active', 'dokan' ) }
                </span>
            );
        }

        return (
            <span
                className={ twMerge(
                    'text-neutral-500',
                    isLoading &&
                        'text-transparent bg-neutral-200 animate-pulse rounded'
                ) }
            >
                { __( 'Closed', 'dokan' ) }
            </span>
        );
    };

    const fields = [
        {
            id: 'id',
            label: __( 'Ticket Id', 'dokan' ),
            render: ( { item }: { item: Ticket } ) => {
                return (
                    <div
                        className={ twMerge(
                            notIsRead( item ) ? 'font-semibold' : '',
                            isLoading &&
                                'text-transparent bg-neutral-200 animate-pulse rounded'
                        ) }
                    >
                        <button
                            className={ twMerge(
                                'dokan-link opacity-100',
                                isLoading && 'opacity-0'
                            ) }
                            type="button"
                            onClick={ () =>
                                navigate( `/vendor-support/${ item.id }` )
                            }
                        >
                            #{ item.id }
                        </button>
                    </div>
                );
            },
            enableSorting: false,
        },
        ...( context === 'admin'
            ? [
                  {
                      id: 'vendor_id',
                      label: __( 'Vendor', 'dokan' ),
                      enableSorting: false,
                      render: ( { item }: { item: Ticket } ) => {
                          return (
                              <div
                                  className={ twMerge(
                                      notIsRead( item ) ? 'font-semibold' : '',
                                      isLoading &&
                                          'text-transparent bg-neutral-200 animate-pulse rounded'
                                  ) }
                              >
                                  { item?.vendor?.store_name ??
                                      __( 'No Name', 'dokan' ) }
                              </div>
                          );
                      },
                  },
              ]
            : [] ),
        {
            id: 'subject',
            label: __( 'Subject', 'dokan' ),
            enableSorting: false,
            render: ( { item }: { item: Ticket } ) => (
                <div
                    className={ twMerge(
                        notIsRead( item ) ? 'font-semibold' : '',
                        isLoading &&
                            'text-transparent bg-neutral-200 animate-pulse rounded'
                    ) }
                >
                    <p className="m-0 space-x-2 flex flex-wrap max-w-80 text-wrap leading-6">
                        { truncate(
                            item.subject,
                            window.wp.hooks.applyFilters(
                                'dokan-frontend-vendor-support-list-item-title-truncate-length',
                                30
                            )
                        ) }
                    </p>
                </div>
            ),
        },
        {
            id: 'status',
            label: __( 'Status', 'dokan' ),
            enableSorting: false,
            render: ( { item }: { item: Ticket } ) => (
                <div
                    className={ twMerge(
                        notIsRead( item ) ? 'font-semibold' : ''
                    ) }
                >
                    { getStatus( item ) }
                </div>
            ),
        },
        {
            id: 'date',
            label: __( 'Date', 'dokan' ),
            enableSorting: false,
            render: ( { item }: { item: Ticket } ) => (
                <div
                    className={ twMerge(
                        notIsRead( item ) ? 'font-semibold' : '',
                        isLoading &&
                            'text-transparent bg-neutral-200 animate-pulse rounded'
                    ) }
                >
                    <DateTimeHtml date={ item.date_created } />
                </div>
            ),
        },
    ];

    const [ view, setView ] = useState( {
        perPage: 10,
        page: 1,
        search: '',
        type: 'table',
        titleField: 'id',
        layout: { ...defaultLayouts },
        fields,
    } );

    const [ paginationInfo, setPaginationInfo ] = useState( {
        totalItems: 0,
        totalPages: 0,
    } );

    /**
     * Fetches tickets from the API based on the current view and filters.
     */
    const getTickets = async () => {
        setIsLoading( true );

        const queryParams = {
            per_page: view.perPage,
            page: view.page,
            search: view.search,
            ...( status !== 'all' && { status } ),
            ...( selectedVendor && { vendor_id: selectedVendor.value } ), // Add vendor_id if a vendor is selected
            ...( selectedDate && { date_from: selectedDate } ),
            ...( selectedDate && { date_to: selectedDate } ),
            ...( currentLogedinVendor &&
                currentLogedinVendor?.id && {
                    vendor_id: currentLogedinVendor.id,
                } ),
        };

        try {
            const response = await apiFetch( {
                method: 'GET',
                path: addQueryArgs(
                    '/dokan/v1/vendor-support/tickets',
                    queryParams
                ),
                parse: false,
            } );

            const countsResult = await apiFetch( {
                method: 'GET',
                path: addQueryArgs( '/dokan/v1/vendor-support/tickets/counts', {
                    ...( selectedVendor && {
                        vendor_id: selectedVendor.value,
                    } ),
                    ...( currentLogedinVendor &&
                        currentLogedinVendor?.id && {
                            vendor_id: currentLogedinVendor.id,
                        } ),
                } ),
                parse: true,
            } );

            setCounts( countsResult );

            const data: Ticket[] = await response.json();
            const totalItems = parseInt(
                response.headers.get( 'X-WP-Total' ) || '0'
            );
            const totalPages = parseInt(
                response.headers.get( 'X-WP-TotalPages' ) || '0'
            );

            setTickets( data );
            setPaginationInfo( { totalItems, totalPages } );
        } catch ( error ) {
            console.error( 'Error fetching tickets:', error );
            // You could add an error state here to show a message to the user
        } finally {
            setIsLoading( false );
        }
    };

    // Re-fetch tickets whenever the view, status, or selected vendor changes.
    useEffect( () => {
        getTickets();
    }, [ view, status, selectedVendor, selectedDate ] );

    const actions = [
        {
            id: 'view',
            isPrimary: true,
            isEligible: () => true,
            supportsBulk: true,
            label: () => (
                <div
                    className={ twMerge(
                        isLoading
                            ? 'text-transparent bg-neutral-200 animate-pulse rounded'
                            : 'dokan-link'
                    ) }
                >
                    { __( 'View', 'dokan' ) }
                </div>
            ),
            callback: ( data: Ticket[] ) => {
                const item = data[ 0 ];
                navigate( `/vendor-support/${ item.id }` );
            },
        },
        {
            id: 'close',
            isPrimary: true,
            isEligible: ( item: Ticket ) => item.status !== 'closed',
            label: () => (
                <div
                    className={ twMerge(
                        isLoading &&
                            'text-transparent bg-neutral-200 animate-pulse rounded'
                    ) }
                >
                    { __( 'Close', 'dokan' ) }
                </div>
            ),
            callback: ( data: Ticket[] ) =>
                setIsConfirmationModalOpen( data[ 0 ].id ),
        },
    ];

    // This function is called by DataViews when pagination or sorting changes.
    const fetchData = ( newView ) => {
        setView( newView );
    };

    // Handler for tab selection
    const handleTabSelect = ( tabName: string ) => {
        setStatus( tabName );
        setView( ( prev ) => ( { ...prev, page: 1 } ) ); // Reset to first page
    };

    // Handler for the search button
    const handleSearch = () => {
        setView( ( prev ) => ( { ...prev, search: searchTerm, page: 1 } ) );
    };

    /**
     * Handles bulk actions on selected tickets.
     */
    const handleBulkAction = async () => {
        if ( ! selectedBulkAction ) {
            return;
        }

        setIsApplyingBulkAction( true );

        let payload = {};

        if ( selectedBulkAction.value === 'mark-all-as-read' ) {
            const readUpdateKey =
                context === 'admin' ? 'is_read_by_admin' : 'is_read_by_vendor';

            const ticketsToUpdate = [];

            tickets.forEach( ( ticket ) => {
                if ( selection.includes( ticket.id ) ) {
                    ticket[ readUpdateKey ] = true;
                    ticketsToUpdate.push( ticket );
                }
            } );

            payload = {
                update: ticketsToUpdate,
            };
        }

        // Delete tickets older than 6 months
        if ( selectedBulkAction.value === 'delete-last-6-months' ) {
            const now = new Date();
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth( now.getMonth() - 6 );

            payload = {
                delete_by_date_range: {
                    date_from: '1970-01-01T00:00:00.000Z',
                    date_to: sixMonthsAgo.toISOString(),
                    ...( context === 'vendor' && {
                        vendor_id: dokan.store.id,
                    } ),
                },
            };
        }

        // Delete tickets older than 1 year
        if ( selectedBulkAction.value === 'delete-last-1-year' ) {
            const now = new Date();
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear( now.getFullYear() - 1 );

            payload = {
                delete_by_date_range: {
                    date_from: '1970-01-01T00:00:00.000Z',
                    date_to: oneYearAgo.toISOString(),
                    ...( context === 'vendor' && {
                        vendor_id: dokan.store.id,
                    } ),
                },
            };
        }

        try {
            await apiFetch( {
                path: '/dokan/v1/vendor-support/tickets/batch',
                method: 'POST',
                data: payload,
            } );

            // On success, refetch tickets and clear selections
            await getTickets();
            setSelection( [] );
            setSelectedBulkAction( '' );
        } catch ( error ) {
            console.error( 'Error applying bulk action:', error );
        } finally {
            setIsApplyingBulkAction( false );
        }
    };

    /**
     * Handles changes to the vendor filter.
     *
     * @param {Object|null} option The selected option or null if cleared.
     */
    const handleVendorChange = ( option ) => {
        setSelectedVendor( option );
        // Reset to the first page when the filter changes
        setView( ( prev ) => ( { ...prev, page: 1 } ) );
    };

    const handleClose = async () => {
        try {
            await apiFetch( {
                path: `/dokan/v1/vendor-support/tickets/${ isConfirmationModalOpen }`,
                method: 'PUT',
                data: {
                    status: 'closed',
                    ...( currentLogedinVendor &&
                        currentLogedinVendor?.id && {
                            vendor_id: currentLogedinVendor.id,
                        } ),
                },
            } );

            await getTickets();
            setIsConfirmationModalOpen( '' );
            toast( {
                type: 'success',
                title: __( 'Ticket closed successfully', 'dokan' ),
            } );
        } catch ( error ) {
            // Handle error appropriately
            setIsConfirmationModalOpen( '' );
        }
    };

    const bulkAction = () => {
        return (
            <>
                <Select
                    placeholder={ __( 'Bulk Action', 'dokan' ) }
                    options={ bulkOptions }
                    value={ selectedBulkAction }
                    onChange={ ( option ) => setSelectedBulkAction( option ) }
                    menuPortalTarget={ document.querySelector( 'body' ) }
                    className="min-w-56 !h-[40px]"
                />
                <DokanButton
                    onClick={ handleBulkAction }
                    disabled={ isApplyingBulkAction || ! selectedBulkAction }
                    className="!h-[40px]"
                    label={ __( 'Apply', 'dokan' ) }
                    loading={ isApplyingBulkAction }
                />
            </>
        );
    };

    const filterByVendor = () => {
        return (
            <VendorAsyncSelect
                placeholder={ __( 'Filter by Vendor', 'dokan' ) }
                value={ selectedVendor }
                onChange={ handleVendorChange }
                cacheOptions
                defaultOptions
                className="min-w-48"
                menuPortalTarget={ document.querySelector( 'body' ) }
            />
        );
    };

    const filterByDate = () => {
        return (
            <WpDatePicker
                onChange={ ( date ) => {
                    setSelectedDate(
                        date ? format( 'Y-m-d 00:00:00', date ) : ''
                    );
                } }
                currentDate={ selectedDate ? selectedDate : new Date() }
                isInvalidDate={ ( date ) => {
                    const today = new Date();
                    today.setHours( 0, 0, 0, 0 );
                    return date > today;
                } }
            >
                <div className="relative">
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <ChevronDown size={ 16 } />
                    </div>
                    <SimpleInput
                        onChange={ () => {} }
                        value={
                            selectedDate
                                ? dateI18n(
                                      getSettings().formats.date,
                                      selectedDate
                                          ? new Date( selectedDate ).setDate(
                                                new Date(
                                                    selectedDate
                                                ).getDate()
                                            )
                                          : '',
                                      getSettings().timezone.string
                                  )
                                : ''
                        }
                        input={ {
                            id: 'dokan-filter-by-date-input',
                            name: 'dokan_filter_by_date_input',
                            type: 'text',
                            autoComplete: 'off',
                            placeholder: __( 'Filter by Date', 'dokan' ),
                            required: true,
                        } }
                        className="dokan-react-search-input"
                        addOnRight={ '\u00A0' }
                        style={ { paddingRight: 36 } }
                    />
                </div>
            </WpDatePicker>
        );
    };

    const searchInput = () => {
        return (
            <>
                <SearchInput
                    input={ {
                        placeholder: __( 'Search Tickets', 'dokan' ),
                    } }
                    value={ searchTerm }
                    onChange={ setSearchTerm }
                />
                <DokanButton className="!h-[40px]" onClick={ handleSearch }>
                    { __( 'Search', 'dokan' ) }
                </DokanButton>
            </>
        );
    };

    const filterByStatus = () => {
        const options: any[] = applyFilters(
            'dokan-vendor-support-filter-by-status-options',
            [
                {
                    value: 'all',
                    label: __( 'All', 'dokan' ),
                },
                {
                    value: 'open',
                    label: __( 'Active', 'dokan' ),
                },
                {
                    value: 'closed',
                    label: __( 'Closed', 'dokan' ),
                },
            ]
        );
        const value = options.find( ( item ) => item.value === status );
        return (
            <>
                <Select
                    placeholder={ __( 'Filter by Status', 'dokan' ) }
                    options={ options }
                    value={ value }
                    onChange={ ( option ) => handleTabSelect( option.value ) }
                    menuPortalTarget={ document.querySelector( 'body' ) }
                />
            </>
        );
    };

    const adminListHeader = () => {
        return (
            <div className="flex flex-row flex-wrap gap-2 justify-between">
                <div className="flex flex-row gap-2 flex-wrap">
                    { bulkAction() }
                    { filterByVendor() }
                    { filterByDate() }
                </div>
                <div className="flex flex-row gap-2">{ searchInput() }</div>
            </div>
        );
    };

    const vendorListHeader = () => {
        return (
            <div className="flex flex-row justify-between gap-2 flex-wrap mb-6">
                <div className="flex flex-row gap-2 flex-wrap">
                    { searchInput() }
                    { filterByDate() }
                    { filterByStatus() }
                </div>
                <NewTicket
                    currentLogedinVendor={ currentLogedinVendor }
                    getTickets={ getTickets }
                />
            </div>
        );
    };

    const statusTabs = () => {
        return (
            <div className="mt-3">
                <DokanTab
                    tabs={ [
                        {
                            name: 'all',
                            title: sprintf(
                                __( 'All(%d)', 'dokan' ),
                                paginationInfo?.totalItems
                            ),
                        },
                        {
                            name: 'open',
                            title: sprintf(
                                __( 'Active(%d)', 'dokan' ),
                                counts?.active
                            ),
                        },
                        {
                            name: 'closed',
                            title: sprintf(
                                __( 'Closed(%d)', 'dokan' ),
                                counts.closed
                            ),
                        },
                    ] }
                    namespace="dokan-admin-dashboard-vendor-support"
                    onSelect={ handleTabSelect }
                />
            </div>
        );
    };

    const fallbackData = [];

    return (
        <div className="dokan-vendor-support-list-container">
            { context === 'admin' && (
                <h1>{ __( 'Vendor Support', 'dokan' ) }</h1>
            ) }

            <Card
                className={ twMerge(
                    'p-6 bg-white',
                    context === 'admin' ? 'mt-4' : ''
                ) }
            >
                { context === 'admin' ? adminListHeader() : vendorListHeader() }
                { context === 'admin' && statusTabs() }
                <div>
                    <DataViews
                        data={ tickets ?? fallbackData }
                        namespace="dokan-admin-dashboard-vendor-support-dataview"
                        defaultLayouts={ { ...defaultLayouts } }
                        fields={ fields }
                        getItemId={ ( item: Ticket ) => item.id }
                        onChangeView={ fetchData }
                        search={ false }
                        paginationInfo={ paginationInfo }
                        view={ {
                            ...view,
                            layout: { ...defaultLayouts },
                            fields: fields.map( ( field ) =>
                                field.id !== view?.titleField ? field.id : ''
                            ),
                        } }
                        actions={ actions }
                        isLoading={ isLoading }
                        selection={ selection }
                        onChangeSelection={ setSelection }
                    />
                </div>
            </Card>

            <DokanModal
                isOpen={ isConfirmationModalOpen !== '' }
                namespace="dokan-vendor-support-close"
                dialogTitle=""
                confirmationTitle={ __( 'Closing this ticket?', 'dokan' ) }
                confirmationDescription={ __(
                    'Are you sure you want to close this ticket and done with this conversation?',
                    'dokan'
                ) }
                confirmButtonText={ __( 'Close', 'dokan' ) }
                cancelButtonText={ __( 'Cancel', 'dokan' ) }
                onConfirm={ handleClose }
                onClose={ () => setIsConfirmationModalOpen( '' ) }
                dialogIcon={
                    <div
                        className={ `flex items-center justify-center flex-shrink-0 w-14 h-14 bg-[#EFEAFF] border border-[#EFEAFF] text-[#7047EB] rounded-full` }
                    >
                        <CircleCheck />
                    </div>
                }
            />

            <DokanToaster />
        </div>
    );
}

export default List;
